import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Container,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import { supabase } from '../../services/supabase';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1a73e8',
      light: '#4285f4',
      dark: '#1557b0',
    },
    secondary: {
      main: '#ea4335',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: 'Google Sans, Roboto, Arial, sans-serif',
    button: {
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8,
  },
});

const ResetPasswordPage: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [redirectLoading, setRedirectLoading] = useState(false); // 리디렉션 로딩 상태 추가
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const navigate = useNavigate();

  // 이 컴포넌트가 렌더링될 때, URL의 토큰을 처리하고 세션을 확인합니다.
  useEffect(() => {
    const processPasswordResetToken = async () => {
      try {
        console.log('비밀번호 재설정 페이지 로딩. URL:', window.location.href);
        
        // 1. 먼저 현재 세션이 있는지 확인
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('세션 가져오기 오류:', sessionError);
          throw sessionError;
        }
        
        // 이미 유효한 세션이 있으면 그대로 진행
        if (sessionData.session) {
          console.log('기존 세션 발견, 비밀번호 변경 페이지로 진행');
          return; // 세션이 있으므로 그대로 진행
        }
        
        // 2. URL에서 토큰 추출 시도 (여러 형식 지원)
        let accessToken = '';
        let refreshToken = '';
        let type = '';
        
        // 2.1 URL 해시에서 토큰 추출 시도
        if (window.location.hash) {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          accessToken = hashParams.get('access_token') || '';
          refreshToken = hashParams.get('refresh_token') || '';
          type = hashParams.get('type') || '';
          
          console.log('URL 해시에서 토큰 추출 시도:', { accessToken: accessToken ? '존재함' : '없음', type });
        }
        
        // 2.2 URL 쿼리 파라미터에서 토큰 추출 시도
        if (!accessToken) {
          const urlParams = new URLSearchParams(window.location.search);
          accessToken = urlParams.get('access_token') || '';
          refreshToken = urlParams.get('refresh_token') || '';
          type = urlParams.get('type') || '';
          
          console.log('URL 쿼리에서 토큰 추출 시도:', { accessToken: accessToken ? '존재함' : '없음', type });
        }
        
        // 2.3 로컬 스토리지에서 임시 저장된 토큰 확인 (Brevo 리디렉션 처리용)
        if (!accessToken) {
          const storedToken = localStorage.getItem('password_reset_token');
          const storedRefreshToken = localStorage.getItem('password_reset_refresh_token');
          const storedType = localStorage.getItem('password_reset_type');
          
          if (storedToken) {
            accessToken = storedToken;
            refreshToken = storedRefreshToken || '';
            type = storedType || 'recovery'; // 기본값으로 recovery 설정
            
            console.log('로컬 스토리지에서 토큰 복원:', { accessToken: '존재함', type });
            
            // 사용 후 삭제
            localStorage.removeItem('password_reset_token');
            localStorage.removeItem('password_reset_refresh_token');
            localStorage.removeItem('password_reset_type');
          }
        }
        
        // 3. Brevo 링크인지 확인 (URL에 sendibt 포함)
        const BASE_URL = process.env.REACT_APP_BASE_URL || window.location.origin;
        const RESET_PASSWORD_PATH = process.env.REACT_APP_RESET_PASSWORD_PATH || '/reset-password';
        const isBrvoLink = window.location.href.includes('sendibt') && window.location.href.includes('/tr/cl/');
        
        if (isBrvoLink) {
          console.log('Brevo 링크 감지, 리디렉션 처리');
          // 현재 URL을 토큰으로 저장하고 리디렉션
          localStorage.setItem('password_reset_token', window.location.href);
          localStorage.setItem('password_reset_type', 'recovery');
          // 리디렉션
          window.location.href = `${BASE_URL}${RESET_PASSWORD_PATH}`;
          return;
        }
        
        console.log('최종 추출된 토큰 정보:', { accessToken: accessToken ? '존재함' : '없음', type });
        
        // 4. 토큰 유효성 검사
        if (!accessToken) {
          console.error('토큰을 찾을 수 없음');
          setSnackbar({
            open: true,
            message: '비밀번호 재설정 링크가 유효하지 않습니다.',
            severity: 'info'
          });
          return;
        }
        
        // 토큰은 있지만 타입이 recovery가 아닌 경우
        if (type !== 'recovery' && type !== '') {
          console.error('유효하지 않은 토큰 타입:', type);
          setSnackbar({
            open: true,
            message: '유효하지 않은 비밀번호 재설정 링크입니다. 다시 시도해주세요.',
            severity: 'error'
          });
          return;
        }
        
        // 기존 세션이 있다면 먼저 로그아웃 처리
        await supabase.auth.signOut();
        
        // 잠시 대기 후 토큰으로 세션 설정
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // 토큰으로 세션 설정
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken || '',
        });
        
        if (error || !data.session) {
          throw error || new Error('세션을 설정할 수 없습니다.');
        }
        
        // 세션 확인
        const { data: sessionCheck } = await supabase.auth.getSession();
        if (!sessionCheck.session) {
          throw new Error('세션이 정상적으로 설정되지 않았습니다.');
        }
        
        // 성공적으로 세션이 설정되었음을 알림
        console.log('비밀번호 재설정 토큰 처리 성공');
        setSnackbar({
          open: true,
          message: '이제 새 비밀번호를 설정해주세요.',
          severity: 'info'
        });
      } catch (error: any) {
        console.error('비밀번호 재설정 토큰 처리 중 오류:', error);
        setSnackbar({
          open: true,
          message: `비밀번호 재설정 세션을 초기화할 수 없습니다: ${error.message || '알 수 없는 오류'}`,
          severity: 'error'
        });
        setTimeout(() => navigate('/'), 3000);
      }
    };
    
    processPasswordResetToken();
  }, [navigate]);

  const validatePassword = (password: string): boolean => {
    if (password.length < 6) {
      setPasswordError('비밀번호는 최소 6자 이상이어야 합니다.');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const validateConfirmPassword = (password: string, confirmPassword: string): boolean => {
    if (password !== confirmPassword) {
      setConfirmPasswordError('비밀번호가 일치하지 않습니다.');
      return false;
    }
    setConfirmPasswordError('');
    return true;
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setSnackbar({
      ...snackbar,
      open: false
    });
    
    // 비밀번호 유효성 검사
    if (!validatePassword(newPassword)) {
      return;
    }
    
    // 비밀번호 확인 검사
    if (newPassword !== confirmNewPassword) {
      setConfirmPasswordError('비밀번호가 일치하지 않습니다.');
      return;
    } else {
      setConfirmPasswordError('');
    }
    
    setLoading(true);
    
    try {
      // 현재 세션 확인
      const { data: sessionData } = await supabase.auth.getSession();
      
      // 세션이 없는 경우 오류 처리
      if (!sessionData.session) {
        // URL에서 토큰 다시 추출 시도
        const fragment = window.location.hash;
        if (fragment) {
          const params = new URLSearchParams(fragment.substring(1));
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');
          
          if (accessToken) {
            // 세션 재설정 시도
            const { data: resetData, error: resetError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken || '',
            });
            
            if (resetError || !resetData.session) {
              throw new Error('세션이 만료되었습니다. 다시 시도해주세요.');
            }
          } else {
            throw new Error('유효한 토큰이 없습니다. 다시 시도해주세요.');
          }
        } else {
          throw new Error('유효한 세션이 없습니다. 다시 시도해주세요.');
        }
      }
      
      // 세션이 확인되면 비밀번호 업데이트 시도
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      // 비밀번호 변경 성공 시 public.users 테이블의 password_changed_from_initial을 true로 업데이트
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData.user) {
        throw userError || new Error('사용자 정보를 가져올 수 없습니다.');
      }
      const userId = userData.user.id;
      const { error: updateError } = await supabase
        .from('users')
        .update({ password_changed_from_initial: true })
        .eq('id', userId);
      if (updateError) {
        throw updateError;
      }

      setSnackbar({
        open: true,
        message: '비밀번호가 성공적으로 재설정되었습니다!',
        severity: 'success'
      });
      
      // 세션 삭제 및 로그아웃
      await supabase.auth.signOut({ scope: 'global' });
      
      // 리디렉션 로딩 상태 활성화
      setRedirectLoading(true);
      
      // 성공 후 로그인 페이지로 이동
      setTimeout(() => {
        window.location.href = '/login';
      }, 3000);

    } catch (error: any) {
      console.error('비밀번호 재설정 중 오류:', error);
      setSnackbar({
        open: true,
        message: `비밀번호 재설정 실패: ${error.message}`,
        severity: 'error'
      });
      
      // 리디렉션 로딩 상태 활성화
      setRedirectLoading(true);
      
      // 오류 발생 시 로그인 페이지로 이동
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      {/* 리디렉션 로딩 오버레이 */}
      {redirectLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}
      
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
              비밀번호 재설정
            </Typography>
            
            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                {snackbar.message}
              </Alert>
            </Snackbar>
            
            <Box component="form" onSubmit={handlePasswordReset} sx={{ mt: 1, width: '100%' }}>
              <TextField
                required
                fullWidth
                name="newPassword"
                placeholder="새 비밀번호"
                type="password"
                id="newPassword"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onBlur={() => validatePassword(newPassword)}
                error={!!passwordError}
                helperText={passwordError}
                disabled={loading}
                size="small" // small size 추가
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '38px',
                    '& fieldset': {
                      borderColor: '#dadce0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#dadce0',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    height: '22px'
                  },
                  mb: 1 // 두 TextField 사이 간격
                }}
              />
              <TextField
                required
                fullWidth
                name="confirmNewPassword"
                placeholder="새 비밀번호 확인"
                type="password"
                id="confirmNewPassword"
                autoComplete="new-password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                onBlur={() => validateConfirmPassword(newPassword, confirmNewPassword)}
                error={!!confirmPasswordError}
                helperText={confirmPasswordError}
                disabled={loading}
                size="small" // small size 추가
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '38px',
                    '& fieldset': {
                      borderColor: '#dadce0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#dadce0',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    height: '22px'
                  },
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : '비밀번호 변경'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default ResetPasswordPage;
